import CompanyImage from "common/components/ui/CompanyImage";
import ProfileImage from "common/components/ui/ProfileImage";
import type { ProfileSearchParams } from "common/types";

import useSearchSourceOptions from "../../../../datahooks/useSearchSourceOptions";
import useSearchSourceParams from "../../../../hooks/useSearchSourceParams";
import ListSelection from "../../Filters/ListSelection";

interface TypeaheadValuesProps {
  optionsKey: keyof Omit<ProfileSearchParams, "keywords">;
  query: string;
  accountId: string;
  searchParams: ProfileSearchParams;
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
}

export default function TypeaheadValues({
  optionsKey,
  query,
  accountId,
  searchParams,
  setSearchParams,
}: TypeaheadValuesProps) {
  const hasSearch = !!query;

  const { addSearchParamValue, setSearchParam } = useSearchSourceParams(
    searchParams,
    setSearchParams,
  );

  // options for companies and followers are returned under a different key
  const searchKey =
    optionsKey === "current_companies" || optionsKey === "past_companies"
      ? "companies"
      : optionsKey === "followers_of"
        ? "followers"
        : optionsKey;

  const { suggestions, followers, companies, schools, ...rest } =
    useSearchSourceOptions({
      accountId,
      searchParams,
      [`${searchKey}Keywords`]: query,
    });

  // applied values are displayed as tags, so they are left out of the selection
  function getSelectionOptions<Option extends { urn: string }>(
    searchResults: Option[] = [],
    suggestedOptions: Option[] = [],
  ) {
    // "followers of" holds a single profile, every other filter holds a list
    const appliedValues =
      optionsKey === "followers_of"
        ? [searchParams.followers_of].filter(Boolean)
        : searchParams[optionsKey];

    return (query ? searchResults : suggestedOptions).filter(
      ({ urn }) => !appliedValues.some((value) => value.urn === urn),
    );
  }
  // search options have no result counts, so no countKey is passed
  // "followers of" holds profiles, companies and schools hold logos
  if (optionsKey === "followers_of") {
    const options = getSelectionOptions(followers, suggestions?.followers_of);

    return (
      <ListSelection
        idKey="urn"
        renderLabel={({ full_name }) => full_name}
        options={options}
        renderIcon={
          hasSearch
            ? ({ picture_url }) => <ProfileImage src={picture_url} size="sm" />
            : undefined
        }
        onSelect={({ urn }) =>
          setSearchParam(
            optionsKey,
            options.find((option) => option.urn === urn),
          )
        }
      />
    );
  }

  if (
    optionsKey === "current_companies" ||
    optionsKey === "past_companies" ||
    optionsKey === "schools"
  ) {
    const options = getSelectionOptions(
      optionsKey === "schools" ? schools : companies,
      suggestions?.[optionsKey],
    );

    return (
      <ListSelection
        idKey="urn"
        renderLabel={({ name }) => name}
        options={options}
        renderIcon={
          hasSearch
            ? ({ logo_url }) => <CompanyImage size="sm" src={logo_url} />
            : undefined
        }
        onSelect={(option) => addSearchParamValue(optionsKey, option)}
      />
    );
  }

  const options = getSelectionOptions(
    rest[optionsKey],
    suggestions?.[optionsKey],
  );

  return (
    <ListSelection
      idKey="urn"
      renderLabel={({ name }) => name}
      options={options}
      onSelect={(option) => addSearchParamValue(optionsKey, option)}
    />
  );
}
